import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Component from '../../components/Component';
import {
  getParticipationForceActive,
  patchParticipationForceActive,
} from '../../services/participationForceActiveApi';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

const isSuccess = (res) => String(res?.statusCode || '') === '10000' || res?.statusCode == null;

const getPayload = (res) => {
  if (res?.data && typeof res.data === 'object' && !Array.isArray(res.data)) return res.data;
  if (res && typeof res === 'object' && !Array.isArray(res)) return res;
  return null;
};

function formatIst(value) {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
}

function entriesChipLabel(mode, status) {
  if (!status?.[`${mode}_enabled`]) return 'Arcade mode disabled';
  if (status?.accepting_submissions?.[mode]) return 'Accepting entries';
  if (status?.match_started && !status?.[`${mode}_force_active`]) return 'Closed (after kickoff)';
  return 'Not accepting entries';
}

function entriesChipColor(mode, status) {
  if (!status?.[`${mode}_enabled`]) return 'warning';
  if (status?.accepting_submissions?.[mode]) return 'success';
  if (status?.match_started && !status?.[`${mode}_force_active`]) return 'default';
  return 'default';
}

function ArcadeParticipationForceActive() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMid = searchParams.get('match_id') || '';

  const [matchIdInput, setMatchIdInput] = useState(initialMid);
  const [status, setStatus] = useState(null);
  const [predictionForceActive, setPredictionForceActive] = useState(false);
  const [fanwarsForceActive, setFanwarsForceActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pollEnabled, setPollEnabled] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState(null);

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const applyStatus = useCallback((payload) => {
    setStatus(payload);
    setPredictionForceActive(!!payload?.prediction_force_active);
    setFanwarsForceActive(!!payload?.fanwars_force_active);
  }, []);

  const load = useCallback(
    async (midRaw) => {
      const mid = String(midRaw || '').trim();
      if (!mid) {
        showSnackbar('Enter a match id.', 'warning');
        return;
      }
      if (!/^\d+$/.test(mid) || Number(mid) <= 0) {
        showSnackbar('Match id must be a positive number.', 'warning');
        return;
      }

      setLoading(true);
      try {
        const res = await getParticipationForceActive(mid);
        if (String(res?.statusCode || '') === '10003') {
          showSnackbar('Session expired. Please login again.', 'error');
          setTimeout(() => {
            window.location = '/login';
          }, 1500);
          return;
        }
        if (!isSuccess(res)) {
          setStatus(null);
          showSnackbar(res?.message || 'Failed to load participation status', 'error');
          return;
        }

        const payload = getPayload(res);
        if (!payload) {
          setStatus(null);
          showSnackbar('Invalid response from server', 'error');
          return;
        }

        applyStatus(payload);
        setSearchParams({ match_id: String(mid) });
      } finally {
        setLoading(false);
      }
    },
    [applyStatus, setSearchParams, showSnackbar],
  );

  useEffect(() => {
    if (initialMid) load(initialMid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pollEnabled || !status?.match_id) return undefined;

    const intervalId = setInterval(() => {
      load(status.match_id);
    }, 45000);

    return () => clearInterval(intervalId);
  }, [pollEnabled, status?.match_id, load]);

  const hasUnsavedChanges = useMemo(() => {
    if (!status) return false;
    return (
      predictionForceActive !== !!status.prediction_force_active ||
      fanwarsForceActive !== !!status.fanwars_force_active
    );
  }, [fanwarsForceActive, predictionForceActive, status]);

  const persist = useCallback(
    async (nextPrediction, nextFanwars) => {
      const matchId = status?.match_id ?? matchIdInput;
      if (!matchId) {
        showSnackbar('Match id is required', 'warning');
        return;
      }

      setSaving(true);
      try {
        const res = await patchParticipationForceActive({
          match_id: Number(matchId),
          prediction_force_active: nextPrediction,
          fanwars_force_active: nextFanwars,
        });
        if (String(res?.statusCode || '') === '10003') {
          showSnackbar('Session expired. Please login again.', 'error');
          setTimeout(() => {
            window.location = '/login';
          }, 1500);
          return;
        }
        if (!isSuccess(res)) {
          showSnackbar(res?.message || 'Update failed', 'error');
          return;
        }

        const payload = getPayload(res);
        if (payload) applyStatus(payload);
        showSnackbar('Participation settings saved.', 'success');
      } finally {
        setSaving(false);
        setConfirmDialog(null);
      }
    },
    [applyStatus, matchIdInput, showSnackbar, status?.match_id],
  );

  const requestSave = () => {
    if (!status || !hasUnsavedChanges) return;

    const turningPredictionOn = predictionForceActive && !status.prediction_force_active;
    const turningFanwarsOn = fanwarsForceActive && !status.fanwars_force_active;
    const turningPredictionOff = !predictionForceActive && status.prediction_force_active;
    const turningFanwarsOff = !fanwarsForceActive && status.fanwars_force_active;

    if (status.match_started && (turningPredictionOn || turningFanwarsOn)) {
      setConfirmDialog({
        type: 'enable',
        onConfirm: () => persist(predictionForceActive, fanwarsForceActive),
      });
      return;
    }

    if (
      (turningPredictionOff && status.accepting_submissions?.prediction) ||
      (turningFanwarsOff && status.accepting_submissions?.fanwars)
    ) {
      const modes = [];
      if (turningPredictionOff && status.accepting_submissions?.prediction) modes.push('Predict Now');
      if (turningFanwarsOff && status.accepting_submissions?.fanwars) modes.push('Fan Wars');
      setConfirmDialog({
        type: 'disable',
        modes,
        onConfirm: () => persist(predictionForceActive, fanwarsForceActive),
      });
      return;
    }

    persist(predictionForceActive, fanwarsForceActive);
  };

  const resetDraft = () => {
    if (!status) return;
    setPredictionForceActive(!!status.prediction_force_active);
    setFanwarsForceActive(!!status.fanwars_force_active);
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Typography variant="h5" component="h3">
                  Extend participation (delayed match)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Re-open Predict Now and Fan Wars join/submit after scheduled kickoff when a match is delayed.
                  API: <code>GET/PATCH /admin/arcade/participation-force-active</code>
                </Typography>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Match ID"
                size="small"
                value={matchIdInput}
                onChange={(e) => setMatchIdInput(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <Button variant="contained" disabled={loading} onClick={() => load(matchIdInput)}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Load'}
              </Button>
              <Button variant="outlined" component={Link} to="/admin/arcade">
                Back to Arcade
              </Button>
              {status ? (
                <FormControlLabel
                  control={<Switch checked={pollEnabled} onChange={(e) => setPollEnabled(e.target.checked)} />}
                  label="Auto-refresh (45s)"
                />
              ) : null}
            </Box>
          </Paper>

          {status ? (
            <>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Chip size="small" label={`Match: ${status.match_id}`} />
                  {status.arcade_match_id ? (
                    <Chip size="small" label={`Arcade match: ${status.arcade_match_id}`} />
                  ) : null}
                  <Chip
                    size="small"
                    label={status.match_started ? 'Scheduled time passed' : 'Before scheduled kickoff'}
                    color={status.match_started ? 'warning' : 'info'}
                  />
                </Box>

                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: 280 }}>Scheduled start (IST)</TableCell>
                      <TableCell>{formatIst(status.date_start)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Scheduled time passed?</TableCell>
                      <TableCell>{status.match_started ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Predict Now — arcade on</TableCell>
                      <TableCell>{status.prediction_enabled ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Fan Wars — arcade on</TableCell>
                      <TableCell>{status.fanwars_enabled ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Predict Now — entries open</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={entriesChipLabel('prediction', status)}
                          color={entriesChipColor('prediction', status)}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Fan Wars — entries open</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={entriesChipLabel('fanwars', status)}
                          color={entriesChipColor('fanwars', status)}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Predict Now — extension on</TableCell>
                      <TableCell>{status.prediction_force_active ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Fan Wars — extension on</TableCell>
                      <TableCell>{status.fanwars_force_active ? 'Yes' : 'No'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Paper>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Extend Predict Now past scheduled start
                    </Typography>
                    {!status.prediction_enabled ? (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        Arcade prediction not enabled for this match.
                      </Alert>
                    ) : null}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={predictionForceActive}
                          disabled={!status.prediction_enabled || saving}
                          onChange={(e) => setPredictionForceActive(e.target.checked)}
                        />
                      }
                      label={predictionForceActive ? 'Extension on' : 'Extension off'}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Extend Fan Wars past scheduled start
                    </Typography>
                    {!status.fanwars_enabled ? (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        Arcade Fan Wars not enabled for this match.
                      </Alert>
                    ) : null}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={fanwarsForceActive}
                          disabled={!status.fanwars_enabled || saving}
                          onChange={(e) => setFanwarsForceActive(e.target.checked)}
                        />
                      }
                      label={fanwarsForceActive ? 'Extension on' : 'Extension off'}
                    />
                  </Paper>
                </Grid>
              </Grid>

              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" onClick={requestSave} disabled={saving || !hasUnsavedChanges}>
                    {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
                  </Button>
                  <Button variant="outlined" onClick={resetDraft} disabled={saving || !hasUnsavedChanges}>
                    Reset
                  </Button>
                  <Button variant="text" onClick={() => load(status.match_id)} disabled={loading || saving}>
                    Refresh
                  </Button>
                </Box>
              </Paper>
            </>
          ) : (
            <Alert severity="info">Enter a match id and load to manage participation extensions.</Alert>
          )}
        </div>
      </div>

      <Dialog open={!!confirmDialog} onClose={() => !saving && setConfirmDialog(null)}>
        <DialogTitle>{confirmDialog?.type === 'enable' ? 'Extend participation?' : 'Close participation?'}</DialogTitle>
        <DialogContent>
          {confirmDialog?.type === 'enable' ? (
            <Typography variant="body2">
              Scheduled kickoff has passed. Users will be able to join and submit again until you turn this off. Turn
              off when the match actually starts.
            </Typography>
          ) : (
            <Typography variant="body2">
              This will close new entries for {confirmDialog?.modes?.join(' and ') || 'the selected mode(s)'}. Users who
              already submitted keep their slips.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => confirmDialog?.onConfirm?.()} disabled={saving}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default ArcadeParticipationForceActive;
